import React, { useRef, useEffect } from 'react';
import { cn } from "@/lib/utils";
import { sounds } from '@/utils/soundEffects';
import { useAuth } from '@/contexts/AuthContext';
import ComboAnimationLayer from './ComboAnimationLayer';
import HashtagTextInput from './HashtagTextInput';
import CommunityPreview from './CommunityPreview';
import { useAnimatedTextarea } from '@/hooks/use-animated-textarea';
import { useRealtimeCommunityExtraction } from '@/hooks/use-realtime-community-extraction';
import { ScrollArea } from '@/components/ui/scroll-area';

interface AnimatedTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  maxLength?: number;
  isUpdateTab?: boolean;
}

const getComboColor = (combo: number) => {
  if (combo >= 20) return "border-orange-500/50"; // Legendary
  if (combo >= 15) return "border-pink-500/50";   // Epic
  if (combo >= 10) return "border-accent/50";     // Rare
  if (combo >= 8) return "border-secondary/50";   // Uncommon
  if (combo >= 5) return "border-primary/50";     // Common
  return "border-white/10";                       // Basic
};

const AnimatedTextarea = ({ 
  value, 
  onChange, 
  maxLength = 280,
  isUpdateTab = false,
  className,
  ...props 
}: AnimatedTextareaProps) => {
  const { user } = useAuth();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  const {
    lastTyped,
    setLastTyped,
    combo,
    setCombo,
    cursorPosition,
    setCursorPosition,
    animatingChars,
    setAnimatingChars,
    nextCharId,
    setNextCharId,
    getComboTier,
  } = useAnimatedTextarea(user);

  // Real-time community extraction
  const {
    predictedCommunities,
    isExtracting,
    hasPredictions
  } = useRealtimeCommunityExtraction(value as string || '');

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [isUpdateTab]);

  const updateCursorPosition = () => {
    if (!textareaRef.current) return;
    const textarea = textareaRef.current;
    const { selectionStart } = textarea;
    const textBeforeCursor = textarea.value.substring(0, selectionStart);
    const lines = textBeforeCursor.split('\n');
    const currentLineLength = lines[lines.length - 1].length;
    
    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseInt(computedStyle.lineHeight);
    const paddingLeft = parseInt(computedStyle.paddingLeft);
    const charWidth = 8;
    
    const x = Math.min(
      paddingLeft + (currentLineLength * charWidth),
      textarea.clientWidth - 100
    );
    const y = (lines.length - 1) * lineHeight;
    
    setCursorPosition({ x, y });
  };

  const autoResize = () => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = 'auto';
    textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const now = Date.now();
    const timeDiff = now - lastTyped;
    const newValue = e.target.value;
    const oldValue = value as string;
    
    if (timeDiff < 300) {
      setCombo(prev => prev + 1); // Removed the cap
      
      if (combo > 2) {
        sounds.post();
        if (newValue.length > oldValue.length) {
          const newChar = newValue[e.target.selectionStart - 1];
          const direction = Math.random() > 0.5 ? 'left' : 'right';
          const charId = nextCharId;
          
          setAnimatingChars(prev => [...prev, { 
            char: newChar, 
            id: charId, 
            direction,
            x: cursorPosition.x,
            y: cursorPosition.y
          }]);
          setNextCharId(prev => prev + 1);
          
          setTimeout(() => {
            setAnimatingChars(prev => prev.filter(c => c.id !== charId));
          }, 500);
        }
      }
    } else {
      setCombo(0);
    }
    
    setLastTyped(now);
    onChange?.(e);
    updateCursorPosition();
    autoResize();
  };

  useEffect(() => {
    autoResize();
  }, [value]);

  return (
    <ScrollArea className="relative max-h-[300px]">
      <div className="relative">
        <HashtagTextInput
          value={value as string}
          onChange={handleChange}
          onCursorPositionChange={updateCursorPosition}
          textareaRef={textareaRef}
          maxLength={maxLength}
          borderColor={isUpdateTab ? "border-accent/30" : getComboColor(combo)}
          placeholder={isUpdateTab 
            ? "Share an update with the community..." 
            : "Say Something Will Ya?"}
          rows={3}
          className={className}
          {...props}
        />
        
        <ComboAnimationLayer
          combo={combo}
          cursorPosition={cursorPosition}
          animatingChars={animatingChars}
        />
        
        <CommunityPreview
          communities={predictedCommunities}
          isExtracting={isExtracting}
          className="px-3 pb-2"
        />
      </div>
    </ScrollArea>
  );
};

export default AnimatedTextarea;
